/**
 * sectionMarkdown
 *
 * Assignment-document markdown uses HTML comment anchors to delimit
 * structure that isn't representable in plain markdown:
 *
 *   <!-- section: NAME -->        top-level section boundary
 *   <!-- subsection: NAME -->     nested subsection boundary
 *
 * Rendering pipelines in different apps (Learning MFE modals, Portal's
 * combined-PDF renderer) share the same source markdown but present it
 * differently. This module holds the shape-agnostic parsing / manipulation
 * utilities every consumer needs: locate anchors, extract or strip a
 * subsection, reposition a subsection block within the document.
 *
 * These functions are pure string transforms — no DOM, no markdown AST —
 * so they run in any environment. The helpers were originally inlined in
 * the Support Guide modal (BLD2-675); extracted here so Portal's
 * combined-PDF pipeline (BLD2-679) can reuse the same rules.
 */

/**
 * Returns the name of the subsection anchor on this line
 * (`<!-- subsection: NAME -->`), or null if the line isn't a subsection
 * anchor. Case-insensitive on the marker; the returned name is lowercased.
 */
export const getSubsectionName = (line: string): string | null => {
  const match = line.match(/^<!--\s*subsection:\s*([\w-]+)\s*-->\s*$/i);
  return match?.[1]?.toLowerCase() ?? null;
};

/**
 * True if this line is a top-level section anchor
 * (`<!-- section: NAME -->`).
 */
export const isSectionBoundary = (line: string): boolean =>
  /^<!--\s*section:\s*[\w-]+\s*-->\s*$/i.test(line);

/**
 * True if this line is a Support-Guide step heading (`## Step N:`).
 * Kept here alongside the anchor detectors because the Support Guide
 * pagination + insertion rules use it together with the anchor helpers.
 */
export const isStepBoundary = (line: string): boolean =>
  /^##\s+Step\s+\d+:/i.test(line);

/**
 * Drop any preamble that sits before the first meaningful block. Useful
 * when the raw markdown starts with an outer `# Title` or metadata that
 * isn't part of the renderable body.
 *
 * A "meaningful block" is a section anchor, a subsection anchor, a step
 * heading, an `##` heading, or a blockquote (`>`). Anything before the
 * first such line is discarded.
 */
export const stripContentBeforeFirstBlock = (markdown: string): string => {
  const lines = markdown.split("\n");
  const firstBlockIndex = lines.findIndex(
    line =>
      isSectionBoundary(line) ||
      getSubsectionName(line) !== null ||
      isStepBoundary(line) ||
      /^##\s+/.test(line) ||
      /^>\s+/.test(line)
  );

  if (firstBlockIndex === -1) {
    return "";
  }

  return lines.slice(firstBlockIndex).join("\n").trim();
};

/**
 * Extract a named subsection out of the markdown. Returns both the
 * remainder (with the subsection removed) and the subsection body.
 *
 * The subsection is bounded: starts at `<!-- subsection: TARGET -->` and
 * ends at whichever comes first — the next `<!-- subsection: ... -->`
 * (any name), the next `<!-- section: ... -->`, or a step heading
 * (`## Step N:`), or the end of the string. The anchor line itself is
 * consumed (not included in the returned subsection body).
 */
export const extractSubsection = (
  markdown: string,
  subsectionName: string
): { content: string; subsection: string } => {
  const lines = markdown.split("\n");
  const contentLines: string[] = [];
  const subsectionLines: string[] = [];
  let isCapturing = false;
  const targetName = subsectionName.toLowerCase();

  for (const line of lines) {
    const currentSubsection = getSubsectionName(line);

    if (
      isCapturing &&
      (currentSubsection !== null ||
        isSectionBoundary(line) ||
        isStepBoundary(line))
    ) {
      isCapturing = false;
    }

    if (!isCapturing && currentSubsection === targetName) {
      isCapturing = true;
      continue;
    }

    if (isCapturing) {
      subsectionLines.push(line);
      continue;
    }

    contentLines.push(line);
  }

  return {
    content: contentLines.join("\n").trim(),
    subsection: subsectionLines.join("\n").trim()
  };
};

/**
 * Convenience wrapper for the common "remove this subsection and discard it"
 * case. Returns the markdown with the named subsection stripped out.
 */
export const stripSubsection = (
  markdown: string,
  subsectionName: string
): string => extractSubsection(markdown, subsectionName).content;

/**
 * Insert a block of markdown immediately before the first
 * `<!-- subsection: TARGET -->` anchor. If the target anchor isn't found
 * the block is appended to the end. The inserted block is padded with
 * blank lines above and below so it isn't glued to its neighbours.
 *
 * Empty/whitespace-only blocks are returned untouched.
 */
export const insertBeforeSubsection = (
  markdown: string,
  targetSubsectionName: string,
  blockToInsert: string
): string => {
  if (!blockToInsert.trim()) {
    return markdown;
  }

  const lines = markdown.split("\n");
  const targetName = targetSubsectionName.toLowerCase();
  const targetIndex = lines.findIndex(
    line => getSubsectionName(line) === targetName
  );
  const insertAt = targetIndex === -1 ? lines.length : targetIndex;

  return [
    ...lines.slice(0, insertAt),
    "",
    blockToInsert,
    "",
    ...lines.slice(insertAt)
  ]
    .join("\n")
    .trim();
};
